import { PracticeMatchState } from '../state/practiceMatchStore';
import { ClientMatchState } from '../state/roomStore';

/**
 * Fake wallet addresses used to key player data in the adapted ClientMatchState.
 * Real components (GameCanvas, HandSelector, CompactRunPanel) look up state by wallet,
 * so we need deterministic pseudo-wallet strings for the tutorial player and AI.
 */
export const TUTORIAL_PLAYER_WALLET = 'TUTRLpLayer11111111111111111111111111111111';
export const TUTORIAL_AI_WALLET = 'TUTRL1A1111111111111111111111111111111111111';

/**
 * Maps a PracticeMatchState (keyed by "player"/"ai") → ClientMatchState (keyed by wallet).
 * This lets the real GameCanvas, HandSelector, and CompactRunPanel render tutorial state
 * without any modifications to those components.
 */
export function adaptPracticeToClientState(
  ps: PracticeMatchState,
  playerW: string = TUTORIAL_PLAYER_WALLET,
  aiW: string = TUTORIAL_AI_WALLET,
): ClientMatchState {
  const remapId = (id: string): string => {
    if (id === 'player') return playerW;
    if (id === 'ai') return aiW;
    return id;
  };

  // Remap playerRunStates keys and add missing pendingScorePercent
  const playerRunStates: ClientMatchState['playerRunStates'] = {};
  if (ps.playerRunStates) {
    for (const [key, rs] of Object.entries(ps.playerRunStates)) {
      playerRunStates[remapId(key)] = {
        ...rs,
        pendingScorePercent: (rs as { pendingScorePercent?: number }).pendingScorePercent ?? 0,
      };
    }
  }

  // Remap playerShopStates keys
  const playerShopStates: ClientMatchState['playerShopStates'] = {};
  if (ps.playerShopStates) {
    for (const [key, ss] of Object.entries(ps.playerShopStates)) {
      playerShopStates[remapId(key)] = ss;
    }
  }

  // Remap deckStates keys
  const deckStates: ClientMatchState['deckStates'] = {};
  if (ps.deckStates) {
    for (const [key, ds] of Object.entries(ps.deckStates)) {
      deckStates[remapId(key)] = ds;
    }
  }

  // Remap timeline player IDs
  const timeline = ps.timeline.map((t) => ({
    ...t,
    player: remapId(t.player),
  }));

  // Synthesize tempoState from playerRunStates
  const tempoState: Record<string, number> = {};
  if (ps.playerRunStates) {
    for (const [key, rs] of Object.entries(ps.playerRunStates)) {
      tempoState[remapId(key)] = rs.tempoStreak ?? 0;
    }
  }

  return {
    matchId: ps.matchId,
    wager: { currency: 'SOL', amountLamports: '0' },
    players: [
      { wallet: playerW, displayName: 'You', avatarSeed: playerW },
      { wallet: aiW, displayName: 'AI (Rookie)', avatarSeed: aiW },
    ],
    targetScore: ps.targetScore,
    currentTurnWallet: ps.currentTurnId ? remapId(ps.currentTurnId) : undefined,
    timeline,
    currentHand: ps.currentHand,
    currentHandOwner: ps.currentHandOwner ? remapId(ps.currentHandOwner) : undefined,
    currentHandSequence: ps.currentHandSequence,
    activeAnomaly: ps.activeAnomaly,
    nextAnomaly: ps.nextAnomaly,
    anomalyHistory: ps.anomalyHistory,
    deckStates,
    turnDeadlineIso: ps.turnDeadlineIso,
    turnSecondsRemaining: ps.turnSecondsRemaining,
    tempoState,
    chatMessages: [],
    playerRunStates,
    playerShopStates,
    phase: ps.phase,
  };
}
